const BACKEND_URI = ((import.meta as any)?.env?.VITE_BACKEND_URI ?? "").replace(/\/$/, "");

import {
    ChatAppResponse,
    ChatAppResponseOrError,
    ChatAppRequest,
    Config,
    SimpleAPIResponse,
    HistoryListApiResponse,
    HistoryApiResponse,
    Meeting,
    MeetingListResponse,
    CreateMeetingRequest,
    FileDestinationFilter,
    ListFilesResponse,
    UploadFileResponse,
    SessionFilePayload,
    Project,
    ProjectListResponse,
    ProjectChatsResponse,
    CreateProjectRequest,
    AddChatToProjectRequest,
    ImprovedTranscriptionResponse,
    ProjectFileMetadata,
    Note,
    NotesListResponse,
    ProfileAnalyticsRequest,
    ProfileAnalyticsResponse,
    CreateNoteRequest,
    UpdateNoteRequest,
    ProjectTopic,
    ProjectTopicsResponse,
    NoteTopicOperation,
    NoteTopicSummary,
    SignNoteRequest,
    GenerateNoteRequest,
    NoteAppRequest,
    NoteSessionsResponse,
    NoteSessionHistoryResponse,
    NoteHistoryResponse,
    NotesSearchResponse,
    ClientProfile,
    ProfileHistoryResponse,
    Company,
    CompanyMember,
    CompanySharedDocument,
    CompanySharedDocumentsResponse,
    UploadCompanySharedDocumentResponse,
    CompanySharedChatPayloadDocument,
    CompanySharedChatPayloadResponse,
    PlanningAssignment,
    PlanningAdminChatRequest,
    PlanningAdminChatResponse,
    GeneratePlanningScheduleRequest,
    PlanningMember,
    PlanningParsedTask,
    PlanningRequestCreateResponse,
    PlanningRequestItem,
    PlanningRequestPreviewResponse,
    PlanningSchedule,
    ClientBrief,
    WorkRules,
    ReportTheme,
    FillFormTemplateResponse,
    FormFilledValues,
    FormTemplateDoc,
    FormTemplateSchema,
    FormTemplateSummary,
    ParseFormTemplateResponse
} from "./models";
import { projectWorkspacePath } from "./projectWorkspaceApi";
import { ensurePlanningResponseOk, planningAdminChatResponseSchema, planningScheduleSchema, planningScheduleListSchema, readPlanningResponse } from "./planningResponses";
import { useLogin } from "../authConfig";
export { importNotesFromPdfStream } from "./importNotes";

export async function getHeaders(idToken: string | undefined): Promise<Record<string, string>> {
    // If using login, add the id token of the logged in account as the authorization
    if (useLogin) {
        if (idToken) {
            return { Authorization: `Bearer ${idToken}` };
        }
    }

    return {};
}

let configPromise: Promise<Config> | null = null;

export async function configApi(): Promise<Config> {
    if (configPromise) {
        return configPromise;
    }

    configPromise = fetch(`${BACKEND_URI}/config`, {
        method: "GET",
        credentials: "same-origin"
    })
        .then(async response => {
            if (!response.ok) {
                throw Error(`Request failed with status ${response.status}: ${response.statusText}`);
            }
            return (await response.json()) as Config;
        })
        .catch(error => {
            configPromise = null;
            throw error;
        });

    return configPromise;
}

export async function askApi(request: ChatAppRequest, idToken: string | undefined): Promise<ChatAppResponse> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/ask`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(request),
        credentials: "same-origin"
    });

    if (response.status > 299 || !response.ok) {
        throw Error(`Request failed with status ${response.status}`);
    }
    const parsedResponse: ChatAppResponseOrError = await response.json();
    if (parsedResponse.error) {
        throw Error(parsedResponse.error);
    }

    return parsedResponse as ChatAppResponse;
}

export async function chatApi(request: ChatAppRequest, shouldStream: boolean, idToken: string | undefined): Promise<Response> {
    let url = `${BACKEND_URI}/chat`;
    if (shouldStream) {
        url += "/stream";
    }
    const headers = await getHeaders(idToken);
    return await fetch(url, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(request),
        credentials: "same-origin"
    });
}

export async function agentApi(request: ChatAppRequest & { agent_type: string }, shouldStream: boolean, idToken: string | undefined): Promise<Response> {
    let url = `${BACKEND_URI}/agent`;
    if (shouldStream) {
        url += "/stream";
    }
    const headers = await getHeaders(idToken);
    return await fetch(url, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(request),
        credentials: "same-origin"
    });
}

type ExportSchedulePayload = {
    content: string;
    title?: string;
    scheduleName?: string;
    ruleNames?: string[];
};

export async function exportSchedulePdfApi(payload: ExportSchedulePayload, idToken: string | undefined): Promise<{ blob: Blob; filename: string }> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/schedule/export`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({
            content: payload.content,
            title: payload.title,
            schedule_name: payload.scheduleName,
            rule_names: payload.ruleNames
        }),
        credentials: "same-origin"
    });

    if (!response.ok) {
        let message = `Export failed with status ${response.status}`;
        try {
            const errorBody = await response.json();
            if (errorBody?.error) {
                message = errorBody.error;
            }
        } catch {
            // ignore parse errors from non-JSON error responses
        }
        throw new Error(message);
    }

    const blob = await response.blob();
    let filename = "optimized-schedule.pdf";
    const disposition = response.headers.get("Content-Disposition");
    if (disposition) {
        const match = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
        if (match?.[1]) {
            filename = match[1].replace(/['"]/g, "");
        }
    }

    return { blob, filename };
}

type ExportAnswerPayload = {
    content: string;
    title?: string;
};

export async function exportAnswerPdfApi(payload: ExportAnswerPayload, idToken: string | undefined): Promise<{ blob: Blob; filename: string }> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/answers/export`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({
            content: payload.content,
            title: payload.title
        }),
        credentials: "same-origin"
    });

    if (!response.ok) {
        let message = `Export failed with status ${response.status}`;
        try {
            const errorBody = await response.json();
            if (errorBody?.error) {
                message = errorBody.error;
            }
        } catch {
            // ignore parse errors from non-JSON error responses
        }
        throw new Error(message);
    }

    const blob = await response.blob();
    let filename = "chat-answer.pdf";
    const disposition = response.headers.get("Content-Disposition");
    if (disposition) {
        const match = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
        if (match?.[1]) {
            filename = match[1].replace(/['"]/g, "");
        }
    }

    return { blob, filename };
}

export async function getSpeechApi(text: string): Promise<string | null> {
    return await fetch(`${BACKEND_URI}/speech`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json"
        },
        body: JSON.stringify({
            text: text
        })
    })
        .then(response => {
            if (response.status == 200) {
                return response.blob();
            } else if (response.status == 400) {
                console.log("Speech synthesis is not enabled.");
                return null;
            } else {
                console.error("Unable to get speech synthesis.");
                return null;
            }
        })
        .then(blob => (blob ? URL.createObjectURL(blob) : null));
}

export type SpeechToTextResponse = {
    text?: string;
    status?: string;
    message?: string;
    error?: string;
    detected_language?: string;
};

export async function speechToTextApi(audioBlob: Blob, fileName: string, idToken: string | undefined, signal?: AbortSignal): Promise<SpeechToTextResponse> {
    const headers = await getHeaders(idToken);
    const formData = new FormData();
    formData.append("audio", audioBlob, fileName);

    const response = await fetch(`${BACKEND_URI}/speech-to-text`, {
        method: "POST",
        credentials: "same-origin",
        headers,
        body: formData,
        signal
    });

    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || response.statusText);
    }

    return (await response.json()) as SpeechToTextResponse;
}

export function getCitationFilePath(citation: string): string {
    return `${BACKEND_URI}/content/${citation}`;
}

// Meeting APIs
export async function getMeetingsApi(idToken: string | undefined, count?: number, continuationToken?: string): Promise<MeetingListResponse> {
    const headers = await getHeaders(idToken);
    const params = new URLSearchParams();
    if (count) params.append("count", count.toString());
    if (continuationToken) params.append("continuation_token", continuationToken);

    const response = await fetch(`${BACKEND_URI}/meetings?${params.toString()}`, {
        method: "GET",
        headers: { ...headers },
        credentials: "same-origin"
    });

    if (!response.ok) {
        throw Error(`Request failed with status ${response.status}: ${response.statusText}`);
    }

    return (await response.json()) as MeetingListResponse;
}

export async function getMeetingApi(meetingId: string, idToken: string | undefined): Promise<Meeting> {
    const headers = await getHeaders(idToken);

    const response = await fetch(`${BACKEND_URI}/meetings/${meetingId}`, {
        method: "GET",
        headers: { ...headers },
        credentials: "same-origin"
    });

    if (!response.ok) {
        throw Error(`Request failed with status ${response.status}: ${response.statusText}`);
    }

    return (await response.json()) as Meeting;
}

export async function createMeetingApi(meeting: CreateMeetingRequest, idToken: string | undefined): Promise<{ id: string }> {
    const headers = await getHeaders(idToken);

    const response = await fetch(`${BACKEND_URI}/meetings`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(meeting),
        credentials: "same-origin"
    });

    if (!response.ok) {
        throw Error(`Request failed with status ${response.status}: ${response.statusText}`);
    }

    return (await response.json()) as { id: string };
}

export async function updateMeetingApi(meetingId: string, meeting: Partial<CreateMeetingRequest>, idToken: string | undefined): Promise<{ success: boolean }> {
    const headers = await getHeaders(idToken);

    const response = await fetch(`${BACKEND_URI}/meetings/${meetingId}`, {
        method: "PUT",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(meeting),
        credentials: "same-origin"
    });

    if (!response.ok) {
        throw Error(`Request failed with status ${response.status}: ${response.statusText}`);
    }

    return (await response.json()) as { success: boolean };
}

export async function deleteMeetingApi(meetingId: string, idToken: string | undefined): Promise<void> {
    const headers = await getHeaders(idToken);

    const response = await fetch(`${BACKEND_URI}/meetings/${meetingId}`, {
        method: "DELETE",
        headers: { ...headers },
        credentials: "same-origin"
    });

    if (!response.ok) {
        throw Error(`Request failed with status ${response.status}: ${response.statusText}`);
    }
}

export async function improveMeetingTranscriptionApi(transcription: string, idToken: string | undefined): Promise<ImprovedTranscriptionResponse> {
    const headers = await getHeaders(idToken);

    const response = await fetch(`${BACKEND_URI}/meetings/improve`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ transcription }),
        credentials: "same-origin"
    });

    if (!response.ok) {
        throw Error(`Request failed with status ${response.status}: ${response.statusText}`);
    }

    return (await response.json()) as ImprovedTranscriptionResponse;
}

// Project APIs
export async function createProjectApi(request: CreateProjectRequest, idToken: string | undefined): Promise<Project> {
    const headers = await getHeaders(idToken);

    const response = await fetch(`${BACKEND_URI}/projects`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(request),
        credentials: "same-origin"
    });

    if (!response.ok) {
        const contentType = response.headers.get("content-type") || "";
        const errorText = (await response.text()).trim();
        let errorMessage = `Request failed with status ${response.status}: ${response.statusText}`;
        if (errorText) {
            if (contentType.includes("application/json")) {
                try {
                    const parsed = JSON.parse(errorText) as { error?: string; message?: string };
                    errorMessage = `Request failed with status ${response.status}: ${parsed.error || parsed.message || errorText}`;
                } catch {
                    errorMessage = `Request failed with status ${response.status}: ${errorText}`;
                }
            } else {
                errorMessage = `Request failed with status ${response.status}: ${errorText}`;
            }
        }
        throw Error(errorMessage);
    }

    return (await response.json()) as Project;
}

export async function getProjectsApi(idToken: string | undefined): Promise<ProjectListResponse> {
    const headers = await getHeaders(idToken);

    const response = await fetch(`${BACKEND_URI}/projects`, {
        method: "GET",
        headers: { ...headers },
        credentials: "same-origin"
    });

    if (!response.ok) {
        throw Error(`Request failed with status ${response.status}: ${response.statusText}`);
    }

    return (await response.json()) as ProjectListResponse;
}

export async function getProjectApi(projectId: string, idToken: string | undefined, organizationId?: string): Promise<Project> {
    const headers = await getHeaders(idToken);

    const response = await fetch(`${BACKEND_URI}${projectWorkspacePath(projectId, undefined, organizationId)}`, {
        method: "GET",
        headers: { ...headers },
        credentials: "same-origin"
    });

    if (!response.ok) {
        throw Error(`Request failed with status ${response.status}: ${response.statusText}`);
    }

    return (await response.json()) as Project;
}

export async function addChatToProjectApi(
    projectId: string,
    request: AddChatToProjectRequest,
    idToken: string | undefined,
    organizationId?: string
): Promise<Project> {
    const headers = await getHeaders(idToken);

    const response = await fetch(`${BACKEND_URI}${projectWorkspacePath(projectId, "chats", organizationId)}`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(request),
        credentials: "same-origin"
    });

    if (!response.ok) {
        throw Error(`Request failed with status ${response.status}: ${response.statusText}`);
    }

    return (await response.json()) as Project;
}

export async function getProjectChatsApi(projectId: string, idToken: string | undefined, organizationId?: string): Promise<ProjectChatsResponse> {
    const headers = await getHeaders(idToken);

    const response = await fetch(`${BACKEND_URI}${projectWorkspacePath(projectId, "chats", organizationId)}`, {
        method: "GET",
        headers: { ...headers },
        credentials: "same-origin"
    });

    if (!response.ok) {
        throw Error(`Request failed with status ${response.status}: ${response.statusText}`);
    }

    return (await response.json()) as ProjectChatsResponse;
}

export async function updateProjectApi(
    projectId: string,
    updates: { name?: string; location_id?: string | null; location_name?: string | null },
    idToken: string | undefined
): Promise<Project> {
    const headers = await getHeaders(idToken);

    const response = await fetch(`${BACKEND_URI}/projects/${projectId}`, {
        method: "PUT",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(updates),
        credentials: "same-origin"
    });

    if (!response.ok) {
        throw Error(`Request failed with status ${response.status}: ${response.statusText}`);
    }

    return (await response.json()) as Project;
}

export async function deleteProjectApi(projectId: string, idToken: string | undefined): Promise<void> {
    const headers = await getHeaders(idToken);

    const response = await fetch(`${BACKEND_URI}/projects/${projectId}`, {
        method: "DELETE",
        headers: { ...headers },
        credentials: "same-origin"
    });

    if (!response.ok) {
        throw Error(`Request failed with status ${response.status}: ${response.statusText}`);
    }
}

export async function updateProjectResponsibilityApi(projectId: string, responsibleUserOids: string[], idToken: string | undefined): Promise<Project> {
    const headers = await getHeaders(idToken);

    const response = await fetch(`${BACKEND_URI}/projects/${projectId}/responsibility`, {
        method: "PATCH",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ responsible_user_oids: responsibleUserOids }),
        credentials: "same-origin"
    });

    if (!response.ok) {
        let message = `Request failed with status ${response.status}: ${response.statusText}`;
        try {
            const errorBody = await response.json();
            if (errorBody?.error && typeof errorBody.error === "string") {
                message = errorBody.error;
            }
        } catch {
            // Ignore parse errors and keep status-based message
        }
        throw Error(message);
    }

    return (await response.json()) as Project;
}

// Profile APIs

export async function updateProjectProfileApi(projectId: string, profile: ClientProfile, idToken: string | undefined): Promise<ClientProfile> {
    const headers = await getHeaders(idToken);

    const response = await fetch(`${BACKEND_URI}/projects/${projectId}/profile`, {
        method: "PUT",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ profile }),
        credentials: "same-origin"
    });

    if (!response.ok) {
        throw Error(`Request failed with status ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();
    return result.profile as ClientProfile;
}

export type ProfileAutoFillRequest = {
    file_ids?: string[];
    language?: string;
};

export type ProfileAutoFillResponse = {
    model_delta: Partial<ClientProfile>;
    files_used: Array<{ id?: string; name?: string; chars_used?: number; truncated?: boolean }>;
    total_chars_processed: number;
    truncated: boolean;
};

export async function autoFillProfileApi(projectId: string, request: ProfileAutoFillRequest, idToken: string | undefined): Promise<ProfileAutoFillResponse> {
    const headers = await getHeaders(idToken);

    const response = await fetch(`${BACKEND_URI}/projects/${projectId}/profile/autofill`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(request),
        credentials: "same-origin"
    });

    if (!response.ok) {
        const errorText = await response.text();
        throw Error(`Auto-fill failed: ${errorText || response.statusText}`);
    }

    return (await response.json()) as ProfileAutoFillResponse;
}

export async function getProfileHistoryApi(projectId: string, idToken: string | undefined): Promise<ProfileHistoryResponse> {
    const headers = await getHeaders(idToken);

    const response = await fetch(`${BACKEND_URI}/projects/${projectId}/profile/history`, {
        method: "GET",
        headers: { ...headers },
        credentials: "same-origin"
    });

    if (!response.ok) {
        throw Error(`Failed to fetch profile history: ${response.status} ${response.statusText}`);
    }

    return await response.json();
}

// Note Session APIs

export async function getProjectNoteSessionsApi(projectId: string, idToken: string | undefined): Promise<NoteSessionsResponse> {
    const headers = await getHeaders(idToken);

    const response = await fetch(`${BACKEND_URI}/projects/${projectId}/note-sessions`, {
        method: "GET",
        headers: { ...headers },
        credentials: "same-origin"
    });

    if (!response.ok) {
        throw Error(`Request failed with status ${response.status}: ${response.statusText}`);
    }

    return (await response.json()) as NoteSessionsResponse;
}

export async function getNoteSessionHistoryApi(sessionId: string, idToken: string | undefined): Promise<NoteSessionHistoryResponse> {
    const headers = await getHeaders(idToken);

    const response = await fetch(`${BACKEND_URI}/note-sessions/${sessionId}`, {
        method: "GET",
        headers: { ...headers },
        credentials: "same-origin"
    });

    if (!response.ok) {
        throw Error(`Request failed with status ${response.status}: ${response.statusText}`);
    }

    return (await response.json()) as NoteSessionHistoryResponse;
}

export async function saveNoteSessionApi(
    projectId: string,
    sessionId: string,
    title: string,
    messages: [string, string][],
    idToken: string | undefined
): Promise<void> {
    const headers = await getHeaders(idToken);

    const response = await fetch(`${BACKEND_URI}/projects/${projectId}/note-sessions`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, title, messages }),
        credentials: "same-origin"
    });

    if (!response.ok) {
        throw Error(`Request failed with status ${response.status}: ${response.statusText}`);
    }
}

export async function updateNoteSessionTitleApi(sessionId: string, title: string, idToken: string | undefined): Promise<void> {
    const headers = await getHeaders(idToken);

    const response = await fetch(`${BACKEND_URI}/note-sessions/${sessionId}`, {
        method: "PUT",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ title }),
        credentials: "same-origin"
    });

    if (!response.ok) {
        throw Error(`Request failed with status ${response.status}: ${response.statusText}`);
    }
}

export async function deleteNoteSessionApi(sessionId: string, idToken: string | undefined): Promise<void> {
    const headers = await getHeaders(idToken);

    const response = await fetch(`${BACKEND_URI}/note-sessions/${sessionId}`, {
        method: "DELETE",
        headers: { ...headers },
        credentials: "same-origin"
    });

    if (!response.ok) {
        throw Error(`Request failed with status ${response.status}: ${response.statusText}`);
    }
}

export async function deleteNoteApi(projectId: string, noteId: string, idToken: string | undefined): Promise<void> {
    const headers = await getHeaders(idToken);

    const response = await fetch(`${BACKEND_URI}/projects/${projectId}/notes/${noteId}`, {
        method: "DELETE",
        headers: { ...headers },
        credentials: "same-origin"
    });

    if (!response.ok) {
        throw Error(`Request failed with status ${response.status}: ${response.statusText}`);
    }
}

export async function listProjectFilesApi(projectId: string, idToken: string | undefined, organizationId?: string): Promise<ProjectFileMetadata[]> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}${projectWorkspacePath(projectId, "files", organizationId)}`, {
        method: "GET",
        headers: { ...headers },
        credentials: "same-origin"
    });

    if (!response.ok) {
        throw new Error(`Listing project files failed: ${response.statusText}`);
    }

    return (await response.json()) as ProjectFileMetadata[];
}

export async function uploadProjectFileApi(projectId: string, file: File, idToken: string | undefined): Promise<ProjectFileMetadata> {
    const headers = await getHeaders(idToken);
    const formData = new FormData();
    formData.append("file", file);

    const response = await fetch(`${BACKEND_URI}/projects/${projectId}/files`, {
        method: "POST",
        headers: { ...headers },
        body: formData,
        credentials: "same-origin"
    });

    if (!response.ok) {
        throw new Error(`Uploading project file failed: ${response.statusText}`);
    }

    return (await response.json()) as ProjectFileMetadata;
}

export async function patchProjectFileMetadataApi(
    projectId: string,
    fileId: string,
    metadata: { documentation_type?: string; description?: string },
    idToken: string | undefined
): Promise<ProjectFileMetadata> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/projects/${projectId}/files/${fileId}`, {
        method: "PATCH",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(metadata),
        credentials: "same-origin"
    });

    if (!response.ok) {
        throw new Error(`Updating project file failed: ${response.statusText}`);
    }

    const data = await response.json();
    return data.file as ProjectFileMetadata;
}

export async function deleteProjectFileApi(projectId: string, fileId: string, idToken: string | undefined): Promise<void> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/projects/${projectId}/files/${fileId}`, {
        method: "DELETE",
        headers: { ...headers },
        credentials: "same-origin"
    });

    if (!response.ok) {
        throw new Error(`Deleting project file failed: ${response.statusText}`);
    }
}

// Notes API functions
export async function noteApi(request: NoteAppRequest, shouldStream: boolean, idToken: string | undefined): Promise<Response> {
    const headers = await getHeaders(idToken);

    const response = await fetch(`${BACKEND_URI}/note${shouldStream ? "/stream" : ""}`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(request),
        credentials: "same-origin"
    });

    if (!response.ok) {
        throw new Error(`Note generation failed: ${response.statusText}`);
    }

    return response;
}

export interface GenerateEndReportOptions {
    user_template?: string;
    extra_context?: string;
    mode?: "fixed" | "template_driven";
}

export async function generateEndReportApi(
    projectId: string,
    idToken: string | undefined,
    signal?: AbortSignal,
    options?: GenerateEndReportOptions
): Promise<Response> {
    const headers = await getHeaders(idToken);

    const init: RequestInit = {
        method: "POST",
        headers,
        credentials: "same-origin",
        signal
    };

    if (options && (options.user_template || options.extra_context || options.mode)) {
        headers["Content-Type"] = "application/json";
        init.body = JSON.stringify({
            user_template: options.user_template ?? "",
            extra_context: options.extra_context ?? "",
            mode: options.mode ?? "fixed"
        });
    }

    const response = await fetch(`${BACKEND_URI}/projects/${encodeURIComponent(projectId)}/end-report`, init);

    if (!response.ok) {
        let payload: any = null;
        try {
            payload = await response.json();
        } catch {
            // ignore parse error — fall back to statusText
        }
        const message = payload?.message || payload?.error || `End report generation failed: ${response.statusText}`;
        throw new Error(message);
    }

    return response;
}

export interface SummaryRequest {
    messages?: { role: string; content: string }[];
    context: {
        overrides?: {
            language?: string;
        };
    };
    session_state?: string;
}

export async function summaryApi(request: SummaryRequest, idToken: string | undefined): Promise<Response> {
    const headers = await getHeaders(idToken);

    const response = await fetch(`${BACKEND_URI}/summary/stream`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(request),
        credentials: "same-origin"
    });

    if (!response.ok) {
        throw new Error(`Summary generation failed: ${response.statusText}`);
    }

    return response;
}

export interface CustomSituationsStreamRequest {
    messages?: { role: string; content: string }[];
    context: {
        overrides?: {
            language?: string;
            project_id?: string;
        };
    };
    session_state?: string | null;
}

export async function customSituationsStreamApi(request: CustomSituationsStreamRequest, idToken: string | undefined): Promise<Response> {
    const headers = await getHeaders(idToken);

    const response = await fetch(`${BACKEND_URI}/situations/custom/stream`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(request),
        credentials: "same-origin"
    });

    if (!response.ok) {
        throw new Error(`Custom situations generation failed: ${response.statusText}`);
    }

    return response;
}

// IBIC Code Analysis API
export type IbicAnalyzeRequest = {
    content: {
        status: string;
        goal: string;
        interventions: string;
    };
};

export type IbicAnalyzeResponse = {
    ibicCategory: string;
    ibicCode: string;
};

export async function analyzeIbicApi(request: IbicAnalyzeRequest, idToken: string | undefined): Promise<IbicAnalyzeResponse> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/ibic/analyze`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(request),
        credentials: "same-origin"
    });

    if (!response.ok) {
        let details = "";
        try {
            details = await response.text();
        } catch {
            /* ignore */
        }
        const suffix = details ? ` - ${details}` : "";
        throw new Error(`IBIC analysis failed (${response.status}): ${response.statusText}${suffix}`);
    }

    return await response.json();
}

// Life Area Generation API
export type LifeAreaGenerateRequest = {
    status: string;
    currentLevel: number;
};

export type LifeAreaGenerateResponse = {
    goal: string;
    goalLevel: number;
    interventions: string;
};

export async function generateLifeAreaApi(request: LifeAreaGenerateRequest, idToken: string | undefined): Promise<LifeAreaGenerateResponse> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/lifearea/generate`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(request),
        credentials: "same-origin"
    });

    if (!response.ok) {
        let details = "";
        try {
            details = await response.text();
        } catch {
            /* ignore */
        }
        const suffix = details ? ` - ${details}` : "";
        throw new Error(`Life area generation failed (${response.status}): ${response.statusText}${suffix}`);
    }

    return await response.json();
}

// Legacy function for backward compatibility - will be removed
export async function generateNoteApi(projectId: string, request: GenerateNoteRequest, idToken: string | undefined): Promise<Response> {
    // Convert to new format
    const noteRequest: NoteAppRequest = {
        messages: request.messages,
        context: {
            overrides: {
                project_id: projectId
            }
        }
    };
    return noteApi(noteRequest, true, idToken);
}

export async function createNoteApi(
    projectId: string,
    request: CreateNoteRequest,
    idToken: string | undefined,
    options?: { clientBrainRef?: { goal_id: string; goal_title: string } | null }
): Promise<Note> {
    const headers = await getHeaders(idToken);
    const body = options?.clientBrainRef ? { ...request, client_brain_ref: options.clientBrainRef } : request;
    const response = await fetch(`${BACKEND_URI}/projects/${projectId}/notes`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(body),
        credentials: "same-origin"
    });

    if (!response.ok) {
        throw new Error(`Creating note failed: ${response.statusText}`);
    }

    const data = await response.json();
    return data;
}

export async function profileAnalyticsApi(
    projectId: string,
    request: ProfileAnalyticsRequest,
    idToken: string | undefined
): Promise<ProfileAnalyticsResponse> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/projects/${projectId}/profile-analytics`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(request),
        credentials: "same-origin"
    });

    if (!response.ok) {
        let message = response.statusText;
        try {
            const body = (await response.json()) as { error?: string };
            if (body.error) {
                message = body.error;
            }
        } catch {
            // ignore JSON parse errors
        }
        throw new Error(message);
    }

    return (await response.json()) as ProfileAnalyticsResponse;
}

export async function getProjectNotesApi(projectId: string, idToken: string | undefined): Promise<NotesListResponse> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/projects/${projectId}/notes`, {
        method: "GET",
        headers: { ...headers },
        credentials: "same-origin"
    });

    if (!response.ok) {
        throw new Error(`Getting project notes failed: ${response.statusText}`);
    }

    const data: NotesListResponse = await response.json();
    return data;
}

export async function getNoteApi(projectId: string, noteId: string, idToken: string | undefined): Promise<Note> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/projects/${projectId}/notes/${noteId}`, {
        method: "GET",
        headers: { ...headers },
        credentials: "same-origin"
    });

    if (!response.ok) {
        throw new Error(`Getting note failed: ${response.statusText}`);
    }

    const data: Note = await response.json();
    return data;
}

export async function getNoteHistoryApi(projectId: string, noteId: string, idToken: string | undefined): Promise<NoteHistoryResponse> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/projects/${projectId}/notes/${noteId}/history`, {
        method: "GET",
        headers: { ...headers },
        credentials: "same-origin"
    });

    if (!response.ok) {
        throw new Error(`Getting note history failed: ${response.statusText}`);
    }

    const data: NoteHistoryResponse = await response.json();
    return data;
}

export async function updateNoteApi(projectId: string, noteId: string, request: UpdateNoteRequest, idToken: string | undefined): Promise<void> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/projects/${projectId}/notes/${noteId}`, {
        method: "PUT",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(request),
        credentials: "same-origin"
    });

    if (!response.ok) {
        throw new Error(`Updating note failed: ${response.statusText}`);
    }
}

export async function getProjectTopicsApi(projectId: string, idToken: string | undefined): Promise<ProjectTopic[]> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/projects/${projectId}/topics`, {
        method: "GET",
        headers,
        credentials: "same-origin"
    });
    if (!response.ok) throw new Error(`Getting project Topics failed: ${response.statusText}`);
    const data: ProjectTopicsResponse = await response.json();
    return data.topics;
}

export async function searchProjectTopicsApi(projectId: string, query: string, idToken: string | undefined): Promise<ProjectTopic[]> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/projects/${projectId}/topics/search`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ query, limit: 8 }),
        credentials: "same-origin"
    });
    if (!response.ok) throw new Error(`Searching project Topics failed: ${response.statusText}`);
    const data: ProjectTopicsResponse = await response.json();
    return data.topics;
}

export async function updateNoteTopicsApi(
    projectId: string,
    noteId: string,
    operations: NoteTopicOperation[],
    idToken: string | undefined
): Promise<NoteTopicSummary> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/projects/${projectId}/notes/${noteId}/topics`, {
        method: "PATCH",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ operations }),
        credentials: "same-origin"
    });
    if (!response.ok) throw new Error(`Updating note Topics failed: ${response.statusText}`);
    return response.json();
}

export async function signNoteApi(projectId: string, noteId: string, request: SignNoteRequest, idToken: string | undefined): Promise<void> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/projects/${projectId}/notes/${noteId}/sign`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(request),
        credentials: "same-origin"
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Sign failed with status ${response.status}`);
    }
}

export async function strikeNoteApi(
    projectId: string,
    noteId: string,
    request: { striker_name: string; reason: string },
    idToken: string | undefined
): Promise<void> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/projects/${projectId}/notes/${noteId}/strike`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(request),
        credentials: "same-origin"
    });

    if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Strike failed with status ${response.status}`);
    }
}

export async function updateNoteFollowUpApi(
    projectId: string,
    noteId: string,
    request: {
        suggestion: string;
        recommended_review_in_days: number | null;
        incident?: import("./models").Incident;
    },
    idToken: string | undefined
): Promise<{ incident: import("./models").Incident }> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/projects/${projectId}/notes/${noteId}/incident/follow_up`, {
        method: "PATCH",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(request),
        credentials: "same-origin"
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Follow-up update failed with status ${response.status}`);
    }
    return response.json();
}

export async function patchNoteWorkflowStepApi(
    projectId: string,
    noteId: string,
    stepId: string,
    request: import("./models").PatchWorkflowStepRequest,
    idToken: string | undefined
): Promise<{ workflow_instance: import("./models").WorkflowInstance }> {
    const headers = await getHeaders(idToken);
    const response = await fetch(
        `${BACKEND_URI}/projects/${projectId}/notes/${noteId}/workflow/steps/${encodeURIComponent(stepId)}`,
        {
            method: "PATCH",
            headers: { ...headers, "Content-Type": "application/json" },
            body: JSON.stringify(request),
            credentials: "same-origin"
        }
    );

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Workflow step update failed with status ${response.status}`);
    }
    return response.json();
}

export async function patchWorkflowSubflowApi(
    projectId: string,
    noteId: string,
    subflowId: string,
    request: import("./models").PatchWorkflowSubflowRequest,
    idToken: string | undefined
): Promise<{ workflow_instance: import("./models").WorkflowInstance }> {
    const headers = await getHeaders(idToken);
    const response = await fetch(
        `${BACKEND_URI}/projects/${projectId}/notes/${noteId}/workflow/subflows/${encodeURIComponent(subflowId)}`,
        {
            method: "PATCH",
            headers: { ...headers, "Content-Type": "application/json" },
            body: JSON.stringify(request),
            credentials: "same-origin"
        }
    );

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Workflow subflow update failed with status ${response.status}`);
    }
    return response.json();
}

export async function patchWorkflowSubflowStepApi(
    projectId: string,
    noteId: string,
    subflowId: string,
    stepId: string,
    request: import("./models").PatchWorkflowSubflowStepRequest,
    idToken: string | undefined
): Promise<{ workflow_instance: import("./models").WorkflowInstance }> {
    const headers = await getHeaders(idToken);
    const response = await fetch(
        `${BACKEND_URI}/projects/${projectId}/notes/${noteId}/workflow/subflows/${encodeURIComponent(subflowId)}/steps/${encodeURIComponent(stepId)}`,
        {
            method: "PATCH",
            headers: { ...headers, "Content-Type": "application/json" },
            body: JSON.stringify(request),
            credentials: "same-origin"
        }
    );

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
            errorData.error || `Workflow subflow step update failed with status ${response.status}`
        );
    }
    return response.json();
}

export async function initWorkflowInstanceApi(
    projectId: string,
    noteId: string,
    workflowInstance: import("./models").WorkflowInstance,
    idToken: string | undefined
): Promise<{ workflow_instance: import("./models").WorkflowInstance }> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/projects/${projectId}/notes/${noteId}/workflow_instance`, {
        method: "PUT",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ workflow_instance: workflowInstance }),
        credentials: "same-origin"
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Workflow instance init failed with status ${response.status}`);
    }
    return response.json();
}

export async function uploadFileApi(request: FormData, idToken?: string): Promise<UploadFileResponse> {
    const response = await fetch(`${BACKEND_URI}/upload`, {
        method: "POST",
        headers: await getHeaders(idToken),
        body: request,
        credentials: "same-origin"
    });

    if (!response.ok) {
        throw new Error(`Uploading files failed: ${response.statusText}`);
    }

    const dataResponse: UploadFileResponse = await response.json();
    return dataResponse;
}

export async function deleteUploadedFileApi(
    filename: string,
    idToken?: string,
    options?: { session_id?: string; file_id?: string; planning_context?: string }
): Promise<SimpleAPIResponse> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/delete_uploaded`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ filename, ...options }),
        credentials: "same-origin"
    });

    if (!response.ok) {
        throw new Error(`Deleting file failed: ${response.statusText}`);
    }

    const dataResponse: UploadFileResponse = await response.json();
    return dataResponse;
}

export async function listUploadedFilesApi(idToken?: string): Promise<string[]> {
    const response = await fetch(`${BACKEND_URI}/list_uploaded`, {
        method: "GET",
        headers: await getHeaders(idToken),
        credentials: "same-origin"
    });

    if (!response.ok) {
        throw new Error(`Listing files failed: ${response.statusText}`);
    }

    const dataResponse: string[] = await response.json();
    return dataResponse;
}

export async function listFilesApi(
    destination: FileDestinationFilter = "all",
    limit: number = 50,
    offset: number = 0,
    idToken?: string
): Promise<ListFilesResponse> {
    const headers = await getHeaders(idToken);
    const params = new URLSearchParams({
        destination,
        limit: limit.toString(),
        offset: offset.toString()
    });

    const response = await fetch(`${BACKEND_URI}/files/list?${params.toString()}`, {
        method: "GET",
        headers,
        credentials: "same-origin"
    });

    if (!response.ok) {
        throw new Error(`Failed to list files: ${response.statusText}`);
    }

    const dataResponse: ListFilesResponse = await response.json();
    return dataResponse;
}

export async function openFileApi(blobPath: string, filename: string, contentType: string, idToken?: string): Promise<{ url: string }> {
    const headers = await getHeaders(idToken);
    const params = new URLSearchParams({
        blob_path: blobPath,
        filename,
        content_type: contentType
    });

    const response = await fetch(`${BACKEND_URI}/files/open?${params.toString()}`, {
        method: "GET",
        headers,
        credentials: "same-origin"
    });

    if (!response.ok) {
        throw new Error(`Failed to open file: ${response.statusText}`);
    }

    const dataResponse: { url: string } = await response.json();
    return dataResponse;
}

export async function attachStoredFileApi(
    blobPath: string,
    filename: string,
    contentType: string,
    idToken?: string
): Promise<UploadFileResponse> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/files/attach`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ blob_path: blobPath, filename, content_type: contentType }),
        credentials: "same-origin"
    });

    if (!response.ok) {
        throw new Error(`Failed to attach file: ${response.statusText}`);
    }

    const dataResponse: UploadFileResponse = await response.json();
    return dataResponse;
}

export async function postChatHistoryApi(item: any, idToken?: string): Promise<any> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/chat_history`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(item),
        credentials: "same-origin"
    });

    if (!response.ok) {
        throw new Error(`Posting chat history failed: ${response.statusText}`);
    }

    const dataResponse: any = await response.json();
    return dataResponse;
}

export async function getChatHistoryListApi(count: number, continuationToken: string | undefined, idToken: string): Promise<HistoryListApiResponse> {
    const headers = await getHeaders(idToken);
    let url = `${BACKEND_URI}/chat_history/sessions?count=${count}`;
    if (continuationToken) {
        url += `&continuation_token=${encodeURIComponent(continuationToken)}`;
    }

    const response = await fetch(url.toString(), {
        method: "GET",
        headers: { ...headers, "Content-Type": "application/json" },
        credentials: "same-origin"
    });

    if (!response.ok) {
        throw new Error(`Getting chat histories failed: ${response.statusText}`);
    }

    const dataResponse: HistoryListApiResponse = await response.json();
    return dataResponse;
}

export async function getChatHistoryApi(id: string, idToken: string): Promise<HistoryApiResponse> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/chat_history/sessions/${id}`, {
        method: "GET",
        headers: { ...headers, "Content-Type": "application/json" },
        credentials: "same-origin"
    });

    if (!response.ok) {
        throw new Error(`Getting chat history failed: ${response.statusText}`);
    }

    const dataResponse: HistoryApiResponse = await response.json();
    return dataResponse;
}

export async function updateChatHistoryApi(id: string, updates: { chat_title?: string }, idToken: string): Promise<any> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/chat_history/sessions/${id}`, {
        method: "PUT",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(updates),
        credentials: "same-origin"
    });

    if (!response.ok) {
        throw new Error(`Updating chat history failed: ${response.statusText}`);
    }

    return await response.json();
}

export async function deleteChatHistoryApi(id: string, idToken: string): Promise<any> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/chat_history/sessions/${id}`, {
        method: "DELETE",
        headers: { ...headers, "Content-Type": "application/json" },
        credentials: "same-origin"
    });

    if (!response.ok) {
        throw new Error(`Deleting chat history failed: ${response.statusText}`);
    }
}

export async function searchChatHistoryApi(query: string, limit: number = 50, idToken: string): Promise<HistoryListApiResponse> {
    const headers = await getHeaders(idToken);
    const params = new URLSearchParams();
    params.append("q", query);
    params.append("limit", limit.toString());

    // Try fast search first, fallback to regular search if it fails
    try {
        const response = await fetch(`${BACKEND_URI}/chat_history/search_fast?${params}`, {
            method: "GET",
            headers: headers,
            credentials: "same-origin"
        });

        if (response.ok) {
            const dataResponse: HistoryListApiResponse = await response.json();
            return dataResponse;
        }
    } catch (error) {
        console.log("Fast search not available, using regular search");
    }

    // Fallback to regular Cosmos DB search
    const response = await fetch(`${BACKEND_URI}/chat_history/search?${params}`, {
        method: "GET",
        headers: headers,
        credentials: "same-origin"
    });

    if (!response.ok) {
        throw new Error(`Searching chat history failed: ${response.statusText}`);
    }

    const dataResponse: HistoryListApiResponse = await response.json();
    return dataResponse;
}

export async function getSuggestionsApi(query: string, limit: number = 5, idToken: string): Promise<{ suggestions: string[] }> {
    const headers = await getHeaders(idToken);
    const params = new URLSearchParams();
    params.append("q", query);
    params.append("limit", limit.toString());

    const response = await fetch(`${BACKEND_URI}/chat_history/suggest?${params}`, {
        method: "GET",
        headers: headers,
        credentials: "same-origin"
    });

    if (!response.ok) {
        // Return empty suggestions on error
        return { suggestions: [] };
    }

    return await response.json();
}

export async function searchNotesApi(
    query: string,
    projectIds?: string[],
    dateFrom?: number,
    dateTo?: number,
    limit: number = 50,
    offset: number = 0,
    idToken?: string
): Promise<NotesSearchResponse> {
    const headers = await getHeaders(idToken);
    const params = new URLSearchParams();
    if (query) params.append("q", query);
    if (projectIds && projectIds.length > 0) params.append("project_ids", projectIds.join(","));
    if (dateFrom) params.append("date_from", dateFrom.toString());
    if (dateTo) params.append("date_to", dateTo.toString());
    params.append("limit", limit.toString());
    params.append("offset", offset.toString());

    const response = await fetch(`${BACKEND_URI}/notes/search?${params}`, {
        method: "GET",
        headers: headers,
        credentials: "same-origin"
    });

    if (!response.ok) {
        throw new Error(`Searching notes failed: ${response.statusText}`);
    }

    return await response.json();
}

// Company APIs
export async function getMyCompany(idToken: string | undefined): Promise<Company | null> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/my-company`, {
        method: "GET",
        headers: { ...headers },
        credentials: "same-origin"
    });

    if (response.status === 404) {
        return null;
    }

    if (!response.ok) {
        throw Error(`Request failed with status ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    return data.company as Company;
}

export async function updateCompanyProfileApi(
    profileDescription: string,
    idToken: string | undefined
): Promise<void> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/my-company/settings`, {
        method: "PATCH",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ profile_description: profileDescription }),
        credentials: "same-origin"
    });

    if (!response.ok) {
        let message = `Request failed with status ${response.status}`;
        try {
            const data = await response.json();
            if (data?.error) message = data.error;
        } catch {
            // ignore JSON parse errors, fall through with default message
        }
        throw new Error(message);
    }
}

export async function getCompanyMembers(idToken: string | undefined): Promise<CompanyMember[]> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/my-company/members`, {
        method: "GET",
        headers: { ...headers },
        credentials: "same-origin"
    });

    if (!response.ok) {
        throw Error(`Request failed with status ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    return data.members as CompanyMember[];
}

export async function addCompanyMemberApi(email: string, role: "admin" | "member", idToken: string | undefined): Promise<CompanyMember> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/my-company/members`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ email, role }),
        credentials: "same-origin"
    });

    if (!response.ok) {
        let message = `Request failed with status ${response.status}`;
        try {
            const data = await response.json();
            if (data?.error) message = data.error;
        } catch {
            // ignore JSON parse errors, fall through with default message
        }
        throw new Error(message);
    }

    const data = await response.json();
    return data.member as CompanyMember;
}

export async function updateCompanyMemberRoleApi(memberId: string, role: "admin" | "member", idToken: string | undefined): Promise<CompanyMember> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/my-company/members/${encodeURIComponent(memberId)}/role`, {
        method: "PATCH",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ role }),
        credentials: "same-origin"
    });

    if (!response.ok) {
        let message = `Request failed with status ${response.status}`;
        try {
            const data = await response.json();
            if (data?.error) message = data.error;
        } catch {
            // ignore JSON parse errors, fall through with default message
        }
        throw new Error(message);
    }

    const data = await response.json();
    return data.member as CompanyMember;
}

export async function getMyClientsApi(idToken: string | undefined, locationId?: string): Promise<Array<{ project_id: string; name: string; location_id?: string; responsible_users: Array<{ oid: string; display_name: string }> }>> {
    const headers = await getHeaders(idToken);
    const params = locationId ? `?location_id=${encodeURIComponent(locationId)}` : "";
    const response = await fetch(`${BACKEND_URI}/api/planning/my-clients${params}`, {
        method: "GET",
        headers: { ...headers },
        credentials: "same-origin"
    });
    await ensurePlanningResponseOk(response);
    return await response.json();
}

export async function previewPlanningRequestApi(
    payload: { raw_text: string; location_id?: string; past_messages?: Array<{ role: string; content: string }> },
    idToken: string | undefined
): Promise<PlanningRequestPreviewResponse> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/api/planning/requests/preview`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        credentials: "same-origin"
    });

    await ensurePlanningResponseOk(response);

    return (await response.json()) as PlanningRequestPreviewResponse;
}

export async function createPlanningRequestApi(
    payload: { raw_text: string; parsed_tasks?: PlanningParsedTask[]; location_id?: string },
    idToken: string | undefined
): Promise<PlanningRequestCreateResponse> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/api/planning/requests`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        credentials: "same-origin"
    });

    await ensurePlanningResponseOk(response);

    return (await response.json()) as PlanningRequestCreateResponse;
}

export async function withdrawPlanningRequestApi(requestId: string, idToken: string | undefined): Promise<{ request_id: string; status: string }> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/api/planning/requests/${requestId}/withdraw`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        credentials: "same-origin"
    });
    await ensurePlanningResponseOk(response);
    return (await response.json()) as { request_id: string; status: string };
}

export async function getPlanningMembersApi(idToken: string | undefined): Promise<PlanningMember[]> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/api/planning/members`, {
        method: "GET",
        headers: { ...headers },
        credentials: "same-origin"
    });

    await ensurePlanningResponseOk(response);

    return (await response.json()) as PlanningMember[];
}

export async function getMyPlanningRequestsApi(idToken: string | undefined): Promise<PlanningRequestItem[]> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/api/planning/requests/mine`, {
        method: "GET",
        headers: { ...headers },
        credentials: "same-origin"
    });

    await ensurePlanningResponseOk(response);

    return (await response.json()) as PlanningRequestItem[];
}

export async function getMyPlanningScheduleApi(params: { start_date?: string; end_date?: string }, idToken: string | undefined): Promise<PlanningAssignment[]> {
    const headers = await getHeaders(idToken);
    const query = new URLSearchParams();
    if (params.start_date) {
        query.set("start_date", params.start_date);
    }
    if (params.end_date) {
        query.set("end_date", params.end_date);
    }
    const response = await fetch(`${BACKEND_URI}/api/planning/my-schedule?${query.toString()}`, {
        method: "GET",
        headers: { ...headers },
        credentials: "same-origin"
    });

    await ensurePlanningResponseOk(response);

    return (await response.json()) as PlanningAssignment[];
}

export async function listPlanningRequestsApi(params: { status?: string; location_id?: string }, idToken: string | undefined): Promise<PlanningRequestItem[]> {
    const headers = await getHeaders(idToken);
    const query = new URLSearchParams();
    if (params.status) {
        query.set("status", params.status);
    }
    if (params.location_id) {
        query.set("location_id", params.location_id);
    }
    const response = await fetch(`${BACKEND_URI}/api/planning/requests?${query.toString()}`, {
        method: "GET",
        headers: { ...headers },
        credentials: "same-origin"
    });

    await ensurePlanningResponseOk(response);

    return (await response.json()) as PlanningRequestItem[];
}

export async function updatePlanningRequestApi(
    requestId: string,
    payload: { status: "approved" | "rejected"; review_note?: string },
    idToken: string | undefined
): Promise<PlanningRequestItem> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/api/planning/requests/${requestId}`, {
        method: "PATCH",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        credentials: "same-origin"
    });

    await ensurePlanningResponseOk(response);

    return (await response.json()) as PlanningRequestItem;
}

export interface InsightItem {
    id: string;
    company_id: string;
    project_id: string;
    client_name: string;
    text: string;
    category: string;
    status: "pending" | "confirmed" | "rejected";
    source_note_ids: string[];
    batch_date: string;
    created_at: string;
    reviewed_by: string | null;
    reviewed_at: string | null;
}

export async function listInsightsApi(status: string, idToken: string | undefined): Promise<InsightItem[]> {
    const headers = await getHeaders(idToken);
    const query = new URLSearchParams({ status });
    const response = await fetch(`${BACKEND_URI}/api/insights?${query.toString()}`, {
        method: "GET",
        headers: { ...headers },
        credentials: "same-origin"
    });

    if (!response.ok) {
        throw Error(`Request failed with status ${response.status}: ${response.statusText}`);
    }

    return (await response.json()) as InsightItem[];
}

export async function reviewInsightApi(
    insightId: string,
    payload: { status: "confirmed" | "rejected" },
    idToken: string | undefined
): Promise<InsightItem> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/api/insights/${insightId}`, {
        method: "PATCH",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        credentials: "same-origin"
    });

    if (!response.ok) {
        throw Error(`Request failed with status ${response.status}: ${response.statusText}`);
    }

    return (await response.json()) as InsightItem;
}

export async function triggerInsightDistillationApi(idToken: string | undefined): Promise<Record<string, unknown>> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/api/insights/distill?force=true`, {
        method: "POST",
        headers: { ...headers },
        credentials: "same-origin"
    });

    if (!response.ok) {
        throw Error(`Request failed with status ${response.status}: ${response.statusText}`);
    }

    return (await response.json()) as Record<string, unknown>;
}

export async function generatePlanningScheduleApi(
    payload: GeneratePlanningScheduleRequest,
    idToken: string | undefined
): Promise<PlanningSchedule> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/api/planning/schedules/generate`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        credentials: "same-origin"
    });


    return readPlanningResponse(response, planningScheduleSchema);
}

export async function listPlanningSchedulesApi(params: { status?: string; location_id?: string }, idToken: string | undefined): Promise<PlanningSchedule[]> {
    const headers = await getHeaders(idToken);
    const query = new URLSearchParams();
    if (params.status) {
        query.set("status", params.status);
    }
    if (params.location_id) {
        query.set("location_id", params.location_id);
    }
    const response = await fetch(`${BACKEND_URI}/api/planning/schedules?${query.toString()}`, {
        method: "GET",
        headers: { ...headers },
        credentials: "same-origin"
    });


    return readPlanningResponse(response, planningScheduleListSchema);
}

export async function revalidatePlanningScheduleApi(scheduleId: string, idToken: string | undefined): Promise<PlanningSchedule> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/api/planning/schedules/${encodeURIComponent(scheduleId)}/revalidate`, {
        method: "POST",
        headers: { ...headers },
        credentials: "same-origin"
    });


    return readPlanningResponse(response, planningScheduleSchema);
}

export async function publishPlanningScheduleApi(scheduleId: string, idToken: string | undefined): Promise<PlanningSchedule> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/api/planning/schedules/${scheduleId}/publish`, {
        method: "POST",
        headers: { ...headers },
        credentials: "same-origin"
    });


    return readPlanningResponse(response, planningScheduleSchema);
}

export async function createEmptyDraftApi(
    body: { start_date: string; end_date: string; location_id?: string },
    idToken: string | undefined
): Promise<PlanningSchedule> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/api/planning/schedules/draft`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(body),
        credentials: "same-origin"
    });


    return readPlanningResponse(response, planningScheduleSchema);
}

export async function forkScheduleAsDraftApi(
    scheduleId: string,
    idToken: string | undefined
): Promise<PlanningSchedule> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/api/planning/schedules/${scheduleId}/fork`, {
        method: "POST",
        headers: { ...headers },
        credentials: "same-origin"
    });


    return readPlanningResponse(response, planningScheduleSchema);
}

export async function addAssignmentToDraftApi(
    scheduleId: string,
    body: {
        employee_id: string;
        employee_name: string;
        task_type: string;
        date: string;
        start_time: string;
        end_time: string;
        client_id?: string;
        client_name?: string;
        location_id?: string;
        notes?: string;
        description?: string;
    },
    idToken: string | undefined
): Promise<PlanningSchedule> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/api/planning/schedules/${scheduleId}/assignments`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(body),
        credentials: "same-origin"
    });


    return readPlanningResponse(response, planningScheduleSchema);
}

export async function updateAssignmentInDraftApi(
    scheduleId: string,
    assignmentId: string,
    body: Record<string, unknown>,
    idToken: string | undefined
): Promise<PlanningSchedule> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/api/planning/schedules/${scheduleId}/assignments/${assignmentId}`, {
        method: "PATCH",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(body),
        credentials: "same-origin"
    });


    return readPlanningResponse(response, planningScheduleSchema);
}

export async function deleteAssignmentFromDraftApi(
    scheduleId: string,
    assignmentId: string,
    idToken: string | undefined
): Promise<PlanningSchedule> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/api/planning/schedules/${scheduleId}/assignments/${assignmentId}`, {
        method: "DELETE",
        headers: { ...headers },
        credentials: "same-origin"
    });


    return readPlanningResponse(response, planningScheduleSchema);
}

export async function getClientBriefApi(
    projectId: string,
    idToken: string | undefined,
    refresh: boolean = false
): Promise<ClientBrief> {
    const headers = await getHeaders(idToken);
    const qs = refresh ? "?refresh=1" : "";
    const response = await fetch(`${BACKEND_URI}/projects/${encodeURIComponent(projectId)}/brief${qs}`, {
        method: "GET",
        headers: { ...headers },
        credentials: "same-origin"
    });

    if (!response.ok) {
        throw Error(`Request failed with status ${response.status}: ${response.statusText}`);
    }

    return (await response.json()) as ClientBrief;
}

export async function adminPlanningChatApi(
    payload: PlanningAdminChatRequest,
    idToken: string | undefined
): Promise<PlanningAdminChatResponse> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/api/planning/admin/chat`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        credentials: "same-origin"
    });


    return readPlanningResponse(response, planningAdminChatResponseSchema);
}

export interface ExtractedRules {
    rules: string[];
    source_summary: string;
    work_rules: WorkRules | null;
    /** True when the LLM call failed; the rest of the fields are empty. */
    extract_failed?: boolean;
}

export async function extractPlanningRulesApi(
    payload: { filename: string; content: string },
    idToken: string | undefined
): Promise<ExtractedRules> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/api/planning/admin/extract-rules`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        credentials: "same-origin"
    });

    await ensurePlanningResponseOk(response);

    return (await response.json()) as ExtractedRules;
}

export async function getCompanyReportThemeApi(idToken: string | undefined): Promise<ReportTheme> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/my-company/report-theme`, {
        method: "GET",
        headers: { ...headers },
        credentials: "same-origin"
    });
    if (!response.ok) {
        throw Error(`Request failed with status ${response.status}: ${response.statusText}`);
    }
    const data = await response.json();
    return data.report_theme as ReportTheme;
}

export async function updateCompanyReportThemeApi(theme: ReportTheme, idToken: string | undefined): Promise<ReportTheme> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/my-company/report-theme`, {
        method: "PUT",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(theme),
        credentials: "same-origin"
    });
    if (!response.ok) {
        let message = `Request failed with status ${response.status}`;
        try {
            const data = await response.json();
            if (data?.error) message = data.error;
        } catch {
            /* fall through */
        }
        throw new Error(message);
    }
    return theme;
}

export async function getCompanyWorkRulesApi(idToken: string | undefined): Promise<WorkRules> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/my-company/work-rules`, {
        method: "GET",
        headers: { ...headers },
        credentials: "same-origin"
    });
    if (!response.ok) {
        throw Error(`Request failed with status ${response.status}: ${response.statusText}`);
    }
    const data = await response.json();
    return data.work_rules as WorkRules;
}

export async function updateCompanyWorkRulesApi(
    workRules: WorkRules,
    idToken: string | undefined
): Promise<WorkRules> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/my-company/work-rules`, {
        method: "PUT",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(workRules),
        credentials: "same-origin"
    });
    if (!response.ok) {
        let message = `Request failed with status ${response.status}`;
        try {
            const data = await response.json();
            if (data?.error) message = data.error;
        } catch {
            /* fall through */
        }
        throw new Error(message);
    }
    const data = await response.json();
    return data.work_rules as WorkRules;
}

export async function getCompanySharedDocuments(idToken: string | undefined): Promise<CompanySharedDocument[]> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/my-company/shared-documents`, {
        method: "GET",
        headers: { ...headers },
        credentials: "same-origin"
    });

    if (!response.ok) {
        throw Error(`Request failed with status ${response.status}: ${response.statusText}`);
    }

    const data = (await response.json()) as CompanySharedDocumentsResponse;
    return Array.isArray(data.documents) ? data.documents : [];
}

export async function getCompanySharedDocumentsChatPayload(idToken: string | undefined): Promise<CompanySharedChatPayloadDocument[]> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/my-company/shared-documents/chat-payload`, {
        method: "GET",
        headers: { ...headers },
        credentials: "same-origin"
    });

    if (!response.ok) {
        throw Error(`Request failed with status ${response.status}: ${response.statusText}`);
    }

    const data = (await response.json()) as CompanySharedChatPayloadResponse;
    return Array.isArray(data.documents) ? data.documents : [];
}

export async function uploadCompanySharedDocument(file: File, idToken: string | undefined): Promise<CompanySharedDocument> {
    const headers = await getHeaders(idToken);
    const formData = new FormData();
    formData.append("file", file);

    const response = await fetch(`${BACKEND_URI}/my-company/shared-documents`, {
        method: "POST",
        headers: { ...headers },
        body: formData,
        credentials: "same-origin"
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw Error(errorData.error || `Upload failed with status ${response.status}`);
    }

    const data = (await response.json()) as UploadCompanySharedDocumentResponse;
    if (!data?.document) {
        throw Error("Upload succeeded but no document metadata was returned.");
    }
    return data.document;
}

export async function deleteCompanySharedDocument(filename: string, idToken: string | undefined): Promise<void> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/my-company/shared-documents`, {
        method: "DELETE",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ filename }),
        credentials: "same-origin"
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw Error(errorData.error || `Delete failed with status ${response.status}`);
    }
}

export async function updateProjectVisibility(projectId: string, visibility: "private" | "company", idToken: string | undefined): Promise<Project> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/projects/${projectId}/visibility`, {
        method: "PUT",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ visibility }),
        credentials: "same-origin"
    });

    if (!response.ok) {
        throw Error(`Request failed with status ${response.status}: ${response.statusText}`);
    }

    return (await response.json()) as Project;
}

export async function getCompanyProjects(idToken: string | undefined): Promise<ProjectListResponse> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/company/projects`, {
        method: "GET",
        headers: { ...headers },
        credentials: "same-origin"
    });

    if (!response.ok) {
        throw Error(`Request failed with status ${response.status}: ${response.statusText}`);
    }

    return (await response.json()) as ProjectListResponse;
}

// User Profile Types
export interface UserProfile {
    display_name: string | null;
    first_name: string | null;
    last_name: string | null;
    phone: string | null;
    department: string[];
    languages: string[];
    skills: string[];
    employment_type: "hourly" | "part-time" | "full-time" | null;
    profile_photo_url: string | null;
}

export interface UserProfileUpdateData {
    display_name?: string;
    first_name?: string;
    last_name?: string;
    phone?: string;
    department?: string[];
    languages?: string[];
    skills?: string[];
    employment_type?: "hourly" | "part-time" | "full-time" | null;
    profile_photo_url?: string;
}

export interface ProfileUpdateResponse {
    success: boolean;
    message: string;
    profile: UserProfile;
}

// User Profile APIs
export async function updateUserProfileApi(data: UserProfileUpdateData, idToken: string | undefined): Promise<ProfileUpdateResponse> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/auth/profile`, {
        method: "PATCH",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(data),
        credentials: "same-origin"
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw Error(errorData.error || `Request failed with status ${response.status}`);
    }

    return (await response.json()) as ProfileUpdateResponse;
}

export async function adminUpdateUserProfileApi(userEmail: string, data: UserProfileUpdateData, idToken: string | undefined): Promise<ProfileUpdateResponse> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/auth/users/${encodeURIComponent(userEmail)}/profile`, {
        method: "PATCH",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(data),
        credentials: "same-origin"
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw Error(errorData.error || `Request failed with status ${response.status}`);
    }

    return (await response.json()) as ProfileUpdateResponse;
}

export async function uploadProfilePhotoApi(file: File, idToken: string | undefined): Promise<{ success: boolean; profile_photo_url: string }> {
    const headers = await getHeaders(idToken);
    const formData = new FormData();
    formData.append("photo", file);

    const response = await fetch(`${BACKEND_URI}/auth/profile/photo`, {
        method: "POST",
        headers: { ...headers },
        body: formData,
        credentials: "same-origin"
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw Error(errorData.error || `Upload failed with status ${response.status}`);
    }

    return await response.json();
}

// ---------------------------------------------------------------------------
// Form templates (AI Draft)
// ---------------------------------------------------------------------------

async function formTemplateJson<T>(response: Response): Promise<T> {
    if (!response.ok) {
        const payload: any = await response.json().catch(() => ({}));
        throw new Error(payload?.message || payload?.error || `Request failed with status ${response.status}`);
    }
    return (await response.json()) as T;
}

export async function listFormTemplatesApi(idToken: string | undefined): Promise<{ templates: FormTemplateSummary[] }> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/api/form-templates/`, {
        method: "GET",
        headers,
        credentials: "same-origin"
    });
    return formTemplateJson(response);
}

export async function getFormTemplateApi(templateId: string, idToken: string | undefined): Promise<FormTemplateDoc> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/api/form-templates/${encodeURIComponent(templateId)}`, {
        method: "GET",
        headers,
        credentials: "same-origin"
    });
    return formTemplateJson(response);
}

export async function parseFormTemplateApi(file: File, idToken: string | undefined): Promise<ParseFormTemplateResponse> {
    const headers = await getHeaders(idToken);
    const formData = new FormData();
    formData.append("file", file);
    const response = await fetch(`${BACKEND_URI}/api/form-templates/parse`, {
        method: "POST",
        headers,
        body: formData,
        credentials: "same-origin"
    });
    return formTemplateJson(response);
}

export async function createFormTemplateApi(
    body: { name: string; description?: string; schema: FormTemplateSchema; source_file_name?: string },
    idToken: string | undefined
): Promise<FormTemplateDoc> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/api/form-templates/`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(body),
        credentials: "same-origin"
    });
    return formTemplateJson(response);
}

export async function updateFormTemplateApi(
    templateId: string,
    body: { name?: string; description?: string; schema?: FormTemplateSchema },
    idToken: string | undefined
): Promise<FormTemplateDoc> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/api/form-templates/${encodeURIComponent(templateId)}`, {
        method: "PUT",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(body),
        credentials: "same-origin"
    });
    return formTemplateJson(response);
}

export async function deleteFormTemplateApi(templateId: string, idToken: string | undefined): Promise<void> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/api/form-templates/${encodeURIComponent(templateId)}`, {
        method: "DELETE",
        headers,
        credentials: "same-origin"
    });
    if (!response.ok) {
        const payload: any = await response.json().catch(() => ({}));
        throw new Error(payload?.message || payload?.error || `Delete failed with status ${response.status}`);
    }
}

export async function fillFormTemplateApi(
    templateId: string,
    body: { project_id: string; extra_context?: string },
    idToken: string | undefined,
    signal?: AbortSignal
): Promise<FillFormTemplateResponse> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/api/form-templates/${encodeURIComponent(templateId)}/fill`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(body),
        credentials: "same-origin",
        signal
    });
    return formTemplateJson(response);
}

export async function exportFormTemplateApi(
    templateId: string,
    body: { project_id: string; filled: FormFilledValues },
    idToken: string | undefined
): Promise<Response> {
    const headers = await getHeaders(idToken);
    const response = await fetch(`${BACKEND_URI}/api/form-templates/${encodeURIComponent(templateId)}/export`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(body),
        credentials: "same-origin"
    });
    if (!response.ok) {
        const payload: any = await response.json().catch(() => ({}));
        throw new Error(payload?.message || payload?.error || `Export failed with status ${response.status}`);
    }
    return response;
}
